import React, { useEffect, useState } from "react";

/**
 * EgressLabelPrinter - Etiqueta profesional de bultos para operarios
 */
const esc = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const buildTransportHtml = (tc) => {
  if (!tc) return "";
  const t1 = tc.intermediate || {};
  const t2 = tc.final || {};
  const hasT1 = t1.companyName || t1.phone || t1.licensePlate;
  const hasT2 = t2.companyName || t2.phone || t2.licensePlate;
  if (!hasT1 && !hasT2) return "";

  let html = "";
  if (hasT1) {
    html += `
      <div class="transport-block">
        <div class="transport-tag t1">T1 — RETIRA</div>
        <div class="transport-details">
          ${t1.companyName ? `<span class="td-item"><strong>${esc(t1.companyName)}</strong></span>` : ""}
          ${t1.phone ? `<span class="td-item">Tel: ${esc(t1.phone)}</span>` : ""}
          ${t1.licensePlate ? `<span class="td-item">Pat: ${esc(t1.licensePlate)}</span>` : ""}
        </div>
      </div>`;
  }
  if (hasT2) {
    html += `
      <div class="transport-block">
        <div class="transport-tag t2">T2 — ENTREGA</div>
        <div class="transport-details">
          ${t2.companyName ? `<span class="td-item"><strong>${esc(t2.companyName)}</strong></span>` : ""}
          ${t2.phone ? `<span class="td-item">Tel: ${esc(t2.phone)}</span>` : ""}
          ${t2.licensePlate ? `<span class="td-item">Pat: ${esc(t2.licensePlate)}</span>` : ""}
        </div>
      </div>`;
  }
  return html;
};

const EgressLabelPrinter = ({ labels, onPrintComplete }) => {
  const [isPrinting, setIsPrinting] = useState(false);
  const [printRequested, setPrintRequested] = useState(false);

  const handlePrintLabels = async () => {
    try {
      setIsPrinting(true);

      const [{ default: jsPDF }, { default: html2canvas }, { default: JsBarcode }] =
        await Promise.all([
          import("jspdf"),
          import("html2canvas"),
          import("jsbarcode"),
        ]);

      const hasRedespacho = labels.some((l) => {
        const m = String(l.shippingMethod || "").toLowerCase();
        if (m !== "redespacho" || !l.thirdPartyCarrier) return false;
        const tc = l.thirdPartyCarrier;
        const i = tc.intermediate || {};
        const f = tc.final || {};
        return !!(i.companyName || i.phone || i.licensePlate || f.companyName || f.phone || f.licensePlate);
      });
      const labelHeightPx = hasRedespacho ? 420 : 320;

      // 1. Contenedor temporal
      const container = document.createElement("div");
      const A4_USABLE_PX = Math.floor((190 * 96) / 25.4);

      container.style.cssText = `
        position: fixed; left: -9999px; top: 0; background: #fff;
        font-family: 'Segoe UI', Arial, sans-serif; width: ${A4_USABLE_PX}px;
        padding: 0; margin: 0; box-sizing: border-box;
      `;

      const style = document.createElement("style");
      style.textContent = `
        .page-container {
          display: flex; flex-wrap: wrap; gap: 10px;
          justify-content: flex-start; padding: 10px;
        }

        /* ── Etiqueta ── */
        .label {
          border: 2px solid #222; border-radius: 6px; padding: 0;
          width: 340px; height: ${labelHeightPx}px; margin: 5px;
          box-sizing: border-box; display: flex; flex-direction: column;
          background: #fff; overflow: hidden;
        }

        /* Header negro */
        .label-header {
          background: #1a1a1a; color: #fff; padding: 8px 12px;
          display: flex; justify-content: space-between; align-items: center;
        }
        .label-header .bulto-num {
          font-size: 18px; font-weight: 800; letter-spacing: 0.5px;
        }
        .label-header .orden {
          font-size: 11px; font-weight: 600; opacity: 0.85;
        }

        /* Operativa badge */
        .operativa-row {
          padding: 5px 12px; background: #f0f0f0;
          border-bottom: 1px solid #ddd;
          display: flex; align-items: center; gap: 6px;
        }
        .op-badge {
          display: inline-flex; align-items: center; height: 18px; padding: 0 9px;
          border-radius: 3px; font-size: 10px; line-height: 1; font-weight: 700;
          text-transform: uppercase; letter-spacing: 0.5px; color: #fff;
        }
        .op-cliente { background: #2e7d32; }
        .op-red_despacho { background: #2563EB; }
        .op-redespacho { background: #e65100; }
        .op-transporte_manual { background: #475569; }
        .op-default { background: #555; }

        /* Body */
        .label-body { padding: 8px 12px; flex: 1; display: flex; flex-direction: column; gap: 6px; }

        /* Destinatario */
        .dest-section {}
        .dest-label { font-size: 8px; font-weight: 700; text-transform: uppercase; color: #888; letter-spacing: 0.8px; margin-bottom: 2px; }
        .dest-name { font-size: 14px; font-weight: 700; color: #111; line-height: 1.2; }
        .dest-detail { font-size: 9px; color: #444; line-height: 1.3; }

        /* Transporte */
        .transport-block { margin-top: 4px; }
        .transport-tag {
          display: inline-block; padding: 1px 6px; border-radius: 2px;
          font-size: 8px; font-weight: 800; color: #fff; letter-spacing: 0.5px;
        }
        .t1 { background: #e65100; }
        .t2 { background: #2563EB; }
        .transport-details { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 2px; }
        .td-item { font-size: 9px; color: #333; background: #f5f5f5; padding: 1px 5px; border-radius: 2px; }

        /* Remitente */
        .remit-section { border-top: 1px dashed #ccc; padding-top: 5px; margin-top: auto; }
        .remit-label { font-size: 8px; font-weight: 700; text-transform: uppercase; color: #888; letter-spacing: 0.8px; margin-bottom: 1px; }
        .remit-line { font-size: 8px; color: #666; line-height: 1.3; }

        /* Footer: barcode + marca */
        .label-footer { padding: 6px 12px 8px; border-top: 1px solid #eee; text-align: center; }
        .barcode-wrap svg { max-width: 90%; height: 44px; }
        .barcode-text { font-size: 9px; color: #555; margin-top: 2px; font-family: monospace; letter-spacing: 1px; }
        .brand { font-size: 12px; font-weight: 800; color: #1a1a1a; letter-spacing: 2px; margin-top: 4px; }
      `;
      container.appendChild(style);

      const pageDiv = document.createElement("div");
      pageDiv.className = "page-container";

      labels.forEach((label, index) => {
        const dest = label.destinatario || {};
        const rawMethod = String(label.shippingMethod || "").toLowerCase();
        const barcodeVal = label.barcode || `${label.orderId || "ORD"}-B${label.index}`;

        // Texto legible de la operativa
        const methodMap = {
          cliente: "Cliente retira",
          retiro_puerta: "Cliente retira",
          red_despacho: "Despacho",
          redespacho: "Redespacho",
          transporte_manual: "Transporte retira",
        };
        const shippingText = methodMap[rawMethod] || (label.shippingMethod || "Estándar").replace(/_/g, " ");

        // Clase del badge
        const badgeClass = rawMethod === "cliente" || rawMethod === "retiro_puerta"
          ? "op-cliente"
          : rawMethod === "redespacho"
            ? "op-redespacho"
            : rawMethod === "red_despacho"
              ? "op-red_despacho"
              : rawMethod === "transporte_manual"
                ? "op-transporte_manual"
                : "op-default";

        // Bloque de transporte: redespacho (T1+T2) o transporte_manual (un solo nombre).
        const manualTransportName = label.thirdPartyCarrier?.intermediate?.companyName || "";
        const transportHtml =
          rawMethod === "redespacho"
            ? buildTransportHtml(label.thirdPartyCarrier)
            : rawMethod === "transporte_manual" && manualTransportName
              ? `<div class="dest-section" style="margin-top:6px">
                  <div class="dest-label">Transporte</div>
                  <div class="dest-name" style="font-size:13px">${esc(manualTransportName)}</div>
                </div>`
              : "";

        const labelDiv = document.createElement("div");
        labelDiv.className = "label";
        labelDiv.innerHTML = `
          <!-- HEADER -->
          <div class="label-header">
            <span class="bulto-num">BULTO ${label.index} / ${label.total}</span>
            <span class="orden">ORDEN #${label.orderId || "—"}</span>
          </div>

          <!-- OPERATIVA -->
          <div class="operativa-row">
            <span class="op-badge ${badgeClass}">${esc(shippingText)}</span>
          </div>

          <!-- BODY -->
          <div class="label-body">
            <!-- Destinatario -->
            <div class="dest-section">
              <div class="dest-label">Destinatario</div>
              <div class="dest-name">${esc(dest.nombre || label.clientName || "—")}</div>
              ${dest.dni ? `<div class="dest-detail">DNI: ${esc(dest.dni)}</div>` : ""}
              ${dest.telefono ? `<div class="dest-detail">Tel: ${esc(dest.telefono)}</div>` : ""}
              ${dest.direccion ? `<div class="dest-detail">${esc(dest.direccion)}${dest.localidad ? `, ${esc(dest.localidad)}` : ""}${dest.cp ? ` (${esc(dest.cp)})` : ""}</div>` : ""}
            </div>

            <!-- Transporte (solo redespacho) -->
            ${transportHtml}

            <!-- Remitente -->
            <div class="remit-section">
              <div class="remit-label">Remitente</div>
              <div class="remit-line">${esc(label.remitente || "—")}</div>
            </div>
          </div>

          <!-- FOOTER -->
          <div class="label-footer">
            <div class="barcode-wrap"><svg id="barcode-${index}"></svg></div>
            <div class="barcode-text">${esc(barcodeVal)}</div>
            <div class="brand">VORAS</div>
          </div>
        `;
        pageDiv.appendChild(labelDiv);
      });

      container.appendChild(pageDiv);
      document.body.appendChild(container);

      // Generar barcodes
      labels.forEach((label, index) => {
        const svgEl = container.querySelector(`#barcode-${index}`);
        if (!svgEl) return;
        const val = label.barcode || `${label.orderId || "ORD"}-B${label.index}`;
        JsBarcode(svgEl, val, { format: "CODE128", width: 1.8, height: 44, displayValue: false, margin: 0 });
      });

      await new Promise((r) => setTimeout(r, 400));

      // 2. Captura
      const canvas = await html2canvas(container, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
      });

      // 3. PDF con corte por fila
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const SCALE = 2;
      const PX_PER_MM = (96 * SCALE) / 25.4;
      const imgWidthMM = canvas.width / PX_PER_MM;
      const yOffsetMM = 10;
      const A4_HEIGHT_PX = (297 - yOffsetMM * 2) * PX_PER_MM;
      const ROW_HEIGHT_PX = (labelHeightPx + 20) * SCALE;

      let currentY = 0;
      let firstPage = true;

      while (currentY < canvas.height - 20 * SCALE) {
        if (!firstPage) pdf.addPage();
        firstPage = false;

        const availableHeight = canvas.height - currentY;
        const pageHeightLimit = Math.min(A4_HEIGHT_PX, availableHeight);
        let rowsThatFit = Math.floor(pageHeightLimit / ROW_HEIGHT_PX);
        if (rowsThatFit === 0) rowsThatFit = 1;

        const sliceHeightPX = Math.min(rowsThatFit * ROW_HEIGHT_PX, availableHeight);
        const tempCanvas = document.createElement("canvas");
        tempCanvas.width = canvas.width;
        tempCanvas.height = sliceHeightPX;

        const ctx = tempCanvas.getContext("2d");
        ctx.drawImage(canvas, 0, currentY, canvas.width, sliceHeightPX, 0, 0, canvas.width, sliceHeightPX);

        const sliceHeightMM = sliceHeightPX / PX_PER_MM;
        pdf.addImage(tempCanvas.toDataURL("image/png"), "PNG", 10, yOffsetMM, imgWidthMM, sliceHeightMM, undefined, "FAST");

        currentY += sliceHeightPX;
        tempCanvas.width = 0;
        tempCanvas.height = 0;
      }

      pdf.save(`Etiquetas_Voras_${labels.length}_bultos.pdf`);

      // 4. Limpieza
      document.body.removeChild(container);
      setIsPrinting(false);
      setPrintRequested(false);
      if (onPrintComplete) onPrintComplete();
    } catch (err) {
      console.error("Error PDF:", err);
      setIsPrinting(false);
      setPrintRequested(false);
    }
  };

  useEffect(() => {
    if (labels?.length && !isPrinting && !printRequested) {
      setPrintRequested(true);
      handlePrintLabels();
    }
  }, [labels]);

  return null;
};

export default EgressLabelPrinter;